import { createFileRoute } from "@tanstack/react-router";
import Page from "@/pages/BlogPostPage";
import { getPostBySlug, getRelatedPosts } from "@/lib/blog";
import { extractFaqs, buildFaqJsonLd } from "@/lib/blog-faq";

import { brand } from "@/config/brand";
const SITE_URL = brand.url;

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = params.slug ? getPostBySlug(params.slug) : null;
    const related = post ? getRelatedPosts(post, 3) : [];
    return { post, related };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;

    // Unknown / unpublished slug: keep it out of the index.
    if (!post) {
      return {
        meta: [
          { title: "Post not found — Digitally Baffled Blog" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }

    const canonicalUrl = `${SITE_URL}/blog/${post.slug}`;
    const rawImage = post.featuredImage ?? "/__l5e/assets-v1/aabeccab-688e-44b7-96b1-aaf70d6a2a8d/social-share-cover.png";
    const ogImage = rawImage.startsWith("http") ? rawImage : `${SITE_URL}${rawImage}`;
    const title = post.seoTitle ?? `${post.title} — Digitally Baffled Blog`;
    const description =
      post.seoDescription ?? post.excerpt ?? `Read ${post.title} on the Digitally Baffled blog.`;

    const articleJsonLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "@id": `${canonicalUrl}#article`,
      headline: post.title,
      name: post.title,
      description: post.excerpt,
      url: canonicalUrl,
      datePublished: post.publishedAt,
      dateModified: post.updatedAt ?? post.publishedAt,
      inLanguage: "en-GB",
      image: { "@type": "ImageObject", url: ogImage, width: 1200, height: 630 },
      ...(post.category ? { articleSection: post.category } : {}),
      ...(post.tags?.length ? { keywords: post.tags.join(", ") } : {}),
      wordCount: post.content ? post.content.split(/\s+/).filter(Boolean).length : undefined,
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", "h2", ".article-excerpt"],
      },
      author: {
        "@type": "Person",
        "@id": `${SITE_URL}/#morgan-antell`,
        name: post.author ?? "Morgan Antell",
        url: `${SITE_URL}/about`,
        jobTitle: "Founder",
        worksFor: { "@type": "Organization", "@id": `${SITE_URL}/#organization` },
      },
      publisher: { "@type": "Organization", "@id": `${SITE_URL}/#organization` },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": canonicalUrl,
        isPartOf: { "@id": `${SITE_URL}/#website` },
      },
      isPartOf: {
        "@type": "Blog",
        "@id": `${SITE_URL}/blog#blog`,
        name: "Digitally Baffled Blog",
        url: `${SITE_URL}/blog`,
      },
    };

    const breadcrumbJsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: canonicalUrl },
      ],
    };

    // FAQPage schema from any "Frequently Asked Questions" section in the post —
    // this is the structured data answer engines (ChatGPT, Gemini, Perplexity,
    // Google AI Overviews) and featured snippets quote most often.
    const faqJsonLd = buildFaqJsonLd(extractFaqs(post.content ?? ""), canonicalUrl);


    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:site_name", content: "Digitally Baffled" },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.excerpt ?? "" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { property: "og:image:secure_url", content: ogImage },
        { property: "og:image:type", content: "image/png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: post.title },
        { property: "og:locale", content: "en_GB" },
        { property: "article:published_time", content: post.publishedAt ?? "" },
        ...(post.updatedAt
          ? [{ property: "article:modified_time", content: post.updatedAt }]
          : []),
        { property: "article:author", content: "Morgan Antell" },
        ...(post.category ? [{ property: "article:section", content: post.category }] : []),
        ...(post.tags?.map((tag) => ({ property: "article:tag", content: tag })) ?? []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: "@digitallybaffld" },
        { name: "twitter:creator", content: "@digitallybaffld" },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: post.excerpt ?? "" },
        { name: "twitter:image", content: ogImage },
        { name: "twitter:image:alt", content: post.title },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(articleJsonLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
        ...(faqJsonLd
          ? [{ type: "application/ld+json", children: JSON.stringify(faqJsonLd) }]
          : []),
      ],
    };
  },
  component: Page,
});
